import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import useTemplates, {
    Template,
    TemplateType,
} from '@/src/Templates/hooks/useTemplates';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import useLicenseTypeManagement from '@/src/Licenses/components/hooks/useLicenseTypeManagement';

/*
 * PE templates admin — experience / processing-flow definitions with
 * org and license-type assignment. The JSON is parse-checked before
 * save (classic's detail page threw on malformed JSON).
 */

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

const EMPTY_DRAFT = {
    name: '',
    description: '',
    videoUrl: '',
    definitionJsonString: '',
    templateType: TemplateType.Experience,
};

export default function AdminTemplatesV2() {
    const {
        listTemplates,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        assignToOrg,
        removeFromOrg,
        assignToLicenseType,
        removeFromLicenseType,
        getOrgAssignments,
        getLicenseTypeAssignments,
    } = useTemplates();
    const { listOrgs } = useOrgs();
    const { fetchLicenseTypes } = useLicenseTypeManagement();

    const [rows, setRows] = useState<Template[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dialog, setDialog] = useState<
        { kind: 'create' } | { kind: 'edit'; row: Template } | null
    >(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [assignFor, setAssignFor] = useState<Template | null>(null);
    const [orgs, setOrgs] = useState<any[]>([]);
    const [licenseTypes, setLicenseTypes] = useState<any[]>([]);
    const [assignedOrgs, setAssignedOrgs] = useState<Set<number>>(
        new Set(),
    );
    const [assignedTypes, setAssignedTypes] = useState<Set<number>>(
        new Set(),
    );
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);

    const anyDialog = dialog != null || assignFor != null;
    const closeDialogs = () => {
        setDialog(null);
        setAssignFor(null);
    };
    const dialogRef = useDialogRef(anyDialog, closeDialogs);

    const load = async () => {
        const data = (await listTemplates().catch(() => [])) ?? [];
        setRows(data);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreate = () => {
        setDraft(EMPTY_DRAFT);
        setDialogError(null);
        setDialog({ kind: 'create' });
    };

    const openEdit = (row: Template) => {
        let pretty = row.definitionJsonString ?? '';
        try {
            pretty = JSON.stringify(JSON.parse(pretty), null, 2);
        } catch {
            /* keep raw — the save gate will surface it */
        }
        setDraft({
            name: row.name ?? '',
            description: row.description ?? '',
            videoUrl: row.videoUrl ?? '',
            definitionJsonString: pretty,
            templateType: row.templateType ?? TemplateType.Experience,
        });
        setDialogError(null);
        setDialog({ kind: 'edit', row });
    };

    const submit = async () => {
        if (!dialog || busy) return;
        if (!draft.name.trim()) {
            setDialogError('Name is required.');
            return;
        }
        try {
            JSON.parse(draft.definitionJsonString);
        } catch {
            setDialogError('Definition is not valid JSON.');
            return;
        }
        setBusy(true);
        setDialogError(null);
        const body = {
            name: draft.name.trim(),
            description: draft.description.trim(),
            videoUrl: draft.videoUrl.trim() || undefined,
            definitionJsonString: draft.definitionJsonString,
            templateType: draft.templateType,
        };
        const ok =
            dialog.kind === 'create'
                ? await createTemplate(body).catch(() => null)
                : await updateTemplate(dialog.row.id, body).catch(
                      () => null,
                  );
        setBusy(false);
        if (ok) {
            setDialog(null);
            toast.success(
                dialog.kind === 'create' ? 'Template created' : 'Saved',
            );
            load();
        } else {
            setDialogError("Couldn't save — try again.");
        }
    };

    const remove = async (row: Template) => {
        if (
            !window.confirm(
                `Delete the "${row.name}" template? Orgs lose access to it immediately.`,
            )
        )
            return;
        const prev = rows;
        setRows((current) => current.filter((r) => r.id !== row.id));
        const ok = await deleteTemplate(row.id)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setRows(prev);
            toast.error("Couldn't delete the template.");
        }
    };

    const openAssignments = async (row: Template) => {
        setAssignFor(row);
        setAssignedOrgs(new Set());
        setAssignedTypes(new Set());
        const [orgRows, typeRows, orgIds, typeIds] = await Promise.all([
            orgs.length > 0
                ? Promise.resolve(orgs)
                : listOrgs().catch(() => []),
            licenseTypes.length > 0
                ? Promise.resolve(licenseTypes)
                : fetchLicenseTypes().catch(() => []),
            getOrgAssignments(row.id).catch(() => []),
            getLicenseTypeAssignments(row.id).catch(() => []),
        ]);
        setOrgs(
            [...(orgRows ?? [])].sort((a: any, b: any) =>
                (a.name ?? '').localeCompare(b.name ?? ''),
            ),
        );
        setLicenseTypes(typeRows ?? []);
        setAssignedOrgs(new Set(orgIds ?? []));
        setAssignedTypes(new Set(typeIds ?? []));
    };

    const toggleAssignment = async (
        kind: 'org' | 'type',
        id: number,
    ) => {
        if (!assignFor) return;
        const set = kind === 'org' ? assignedOrgs : assignedTypes;
        const setter = kind === 'org' ? setAssignedOrgs : setAssignedTypes;
        const has = set.has(id);
        setter((current) => {
            const next = new Set(current);
            if (has) next.delete(id);
            else next.add(id);
            return next;
        });
        const call =
            kind === 'org'
                ? has
                    ? removeFromOrg(assignFor.id, id)
                    : assignToOrg(assignFor.id, id)
                : has
                ? removeFromLicenseType(assignFor.id, id)
                : assignToLicenseType(assignFor.id, id);
        const ok = await call.then(() => true).catch(() => false);
        if (!ok) {
            setter((current) => {
                const next = new Set(current);
                if (has) next.add(id);
                else next.delete(id);
                return next;
            });
            toast.error("Couldn't update the assignment.");
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <>
            <SettingsCard
                title="Templates"
                desc="Experience and processing-flow definitions orgs can start from. Assign each to the orgs or license types that should see it."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {rows.length} template
                            {rows.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            onClick={openCreate}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            New template
                        </button>
                    </>
                }
            >
                {rows.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        No templates yet.
                    </p>
                )}
                {rows.map((row) => (
                    <div
                        key={row.id}
                        className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                    >
                        <span className="min-w-0 flex-1 truncate font-semibold">
                            {row.name}
                        </span>
                        <span
                            className={
                                'rounded-full border px-2 py-[2px] font-mono text-[9px] font-semibold tracking-[0.1em] ' +
                                (row.templateType ===
                                TemplateType.Experience
                                    ? 'border-brand-soft bg-brand-soft text-brand-ink'
                                    : 'border-border text-muted-foreground')
                            }
                        >
                            {row.templateType === TemplateType.Experience
                                ? 'EXPERIENCE'
                                : 'PROCESSING'}
                        </span>
                        <span className="whitespace-nowrap font-mono text-[10px] text-faint">
                            v{row.version}
                        </span>
                        <span className="hidden whitespace-nowrap text-[11.5px] text-faint sm:block">
                            {moment(row.updatedAt).format('MMM D')}
                        </span>
                        <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                            <button
                                type="button"
                                onClick={() => openEdit(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                            >
                                edit
                            </button>
                            <button
                                type="button"
                                onClick={() => openAssignments(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                            >
                                access
                            </button>
                            <button
                                type="button"
                                title="Delete template"
                                onClick={() => remove(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    </div>
                ))}
            </SettingsCard>

            {dialog && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={closeDialogs}
                >
                    <div
                        ref={dialogRef}
                        {...dialogPanelProps(
                            dialog.kind === 'create'
                                ? 'New template'
                                : `Edit ${dialog.row.name}`,
                        )}
                        className="flex max-h-[86vh] w-full max-w-[560px] flex-col overflow-y-auto rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-3.5">
                            {dialog.kind === 'create'
                                ? 'New template'
                                : `Edit ${dialog.row.name}`}
                        </div>
                        <div className="mb-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className={FIELD_LABEL}>Name</label>
                                <input
                                    autoFocus
                                    className="ps-input w-full text-[13px]"
                                    value={draft.name}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            name: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <label className={FIELD_LABEL}>Type</label>
                                <select
                                    className="ps-input w-full cursor-pointer appearance-none text-[13px]"
                                    value={draft.templateType}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            templateType: +e.target
                                                .value as TemplateType,
                                        }))
                                    }
                                >
                                    <option
                                        value={TemplateType.Experience}
                                    >
                                        Experience
                                    </option>
                                    <option
                                        value={TemplateType.Processing}
                                    >
                                        Processing flow
                                    </option>
                                </select>
                            </div>
                        </div>
                        <label className={FIELD_LABEL}>Description</label>
                        <textarea
                            className="ps-input mb-3 h-[56px] w-full resize-y text-[13px]"
                            value={draft.description}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    description: e.target.value,
                                }))
                            }
                        />
                        <label className={FIELD_LABEL}>
                            Video URL{' '}
                            <span className="font-normal text-faint">
                                — optional
                            </span>
                        </label>
                        <input
                            className="ps-input mb-3 w-full font-mono text-[12px]"
                            value={draft.videoUrl}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    videoUrl: e.target.value,
                                }))
                            }
                        />
                        <label className={FIELD_LABEL}>
                            Definition JSON
                        </label>
                        <textarea
                            spellCheck={false}
                            className="ps-input mb-4 h-[240px] w-full resize-y whitespace-pre font-mono text-[11.5px] leading-relaxed"
                            value={draft.definitionJsonString}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    definitionJsonString: e.target.value,
                                }))
                            }
                        />
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={busy}
                                onClick={submit}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy ? 'Saving…' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={closeDialogs}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                            {dialogError && (
                                <span className="text-[12px] font-semibold text-attention">
                                    {dialogError}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {assignFor && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={closeDialogs}
                >
                    <div
                        ref={dialogRef}
                        {...dialogPanelProps(`Access — ${assignFor.name}`)}
                        className="w-full max-w-[440px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-1">
                            Access — {assignFor.name}
                        </div>
                        <p className="mb-3 text-[12px] text-faint">
                            Toggles apply immediately.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="ps-label mb-1.5">
                                    Organizations
                                </div>
                                <div className="max-h-[260px] overflow-y-auto">
                                    {orgs.map((o: any) => (
                                        <label
                                            key={o.id}
                                            className="flex cursor-pointer items-center gap-2 rounded-[8px] px-1.5 py-[6px] text-[12.5px] hover:bg-secondary"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={assignedOrgs.has(
                                                    o.id,
                                                )}
                                                onChange={() =>
                                                    toggleAssignment(
                                                        'org',
                                                        o.id,
                                                    )
                                                }
                                            />
                                            <span className="min-w-0 truncate">
                                                {o.name}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="ps-label mb-1.5">
                                    License types
                                </div>
                                <div className="max-h-[260px] overflow-y-auto">
                                    {licenseTypes.map((t: any) => (
                                        <label
                                            key={t.id}
                                            className="flex cursor-pointer items-center gap-2 rounded-[8px] px-1.5 py-[6px] text-[12.5px] hover:bg-secondary"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={assignedTypes.has(
                                                    t.id,
                                                )}
                                                onChange={() =>
                                                    toggleAssignment(
                                                        'type',
                                                        t.id,
                                                    )
                                                }
                                            />
                                            <span className="min-w-0 truncate">
                                                {t.name}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={closeDialogs}
                            className="mt-4 cursor-pointer rounded-full border-0 bg-foreground px-5 py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
